import { useEffect, useState } from "react";
import { CurrencyInr, Bank, FloppyDisk, X, Trash, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SOURCES = ["PNB", "UCO", "PSB", "Cash", "Other"];

export function PaymentStatusPill({ status, isReceipt = false }) {
  const map = {
    paid:        { c: "#0D9488", bg: "#0D948815", label: isReceipt ? "Received" : "Paid" },
    partial:     { c: "#F4A261", bg: "#F4A26115", label: "Partial" },
    outstanding: { c: "#D90429", bg: "#D9042915", label: "Outstanding" },
    "n/a":       { c: "#717680", bg: "#71768015", label: "—" },
  };
  const s = map[status] || map["n/a"];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono-shelv tracking-widest uppercase font-bold border"
      style={{ color: s.c, background: s.bg, borderColor: s.c + "40" }}
      data-testid={`pay-status-${status}`}
    >
      {s.label}
    </span>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentModal({ open, onOpenChange, parentType, parentId, parentLabel = "", onUpdated }) {
  // Sales-side documents (invoice / tax_invoice / quotation / proforma) use
  // receipt language ("Received", "Received By"); purchase-side (work_order,
  // purchase, purchase_bill) keep "Paid" since money is going out.
  const isReceipt = parentType === "invoice" || parentType === "tax_invoice" || parentType === "quotation" || parentType === "proforma" || parentType === "sales_invoice";
  const VERB_PAST      = isReceipt ? "Received"    : "Paid";
  const VERB_BY        = isReceipt ? "Received from" : "Paid by";
  const VERB_ADD_TITLE = isReceipt ? "Add new receipt" : "Add new payment";
  const VERB_ACTION    = isReceipt ? "Record Receipt" : "Add Payment";
  const HISTORY_LABEL  = isReceipt ? "Receipt history" : "Payment history";
  const HDR_BY         = isReceipt ? "From" : "By";
  const HDR_DATE       = isReceipt ? "Received on" : "Paid on";

  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ amount: 0, paid_on: today(), paid_by: "Mukesh Panchal", source: "PNB", reference_no: "", notes: "", advice_url: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!parentId || !parentType) return;
    try {
      const { data } = await api.get("/finance/payments", { params: { parent_type: parentType, parent_id: parentId } });
      setSummary(data);
      if (data.outstanding) setForm((f) => ({ ...f, amount: data.outstanding }));
    } catch (e) { toast.error(`Couldn't load ${isReceipt ? "receipt" : "payment"} history`); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, parentId, parentType]);

  const save = async () => {
    if (Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (!form.paid_by) { toast.error(`${VERB_BY} required`); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/finance/payments", { parent_type: parentType, parent_id: parentId, ...form, amount: Number(form.amount) });
      setSummary(data);
      toast.success(isReceipt ? "Receipt recorded" : "Payment recorded");
      setForm({ amount: data.outstanding, paid_on: today(), paid_by: form.paid_by, source: form.source, reference_no: "", notes: "", advice_url: "" });
      if (onUpdated) onUpdated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${isReceipt ? "receipt" : "payment"}?`)) return;
    try {
      const { data } = await api.delete(`/finance/payments/${id}`);
      setSummary(data);
      toast.success(isReceipt ? "Receipt deleted" : "Payment deleted");
      if (onUpdated) onUpdated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CurrencyInr size={20} weight="bold" className="text-[#D90429]"/> {isReceipt ? "Receipts" : "Payments"} · {parentLabel}</DialogTitle>
        </DialogHeader>

        {summary && (
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div className="border border-black/10 p-3"><div className="overline text-[9px]">Total</div><div className="font-display text-xl font-bold tracking-tight">₹{Number(summary.total).toLocaleString("en-IN")}</div></div>
            <div className="border border-[#0D9488]/30 p-3 bg-[#0D9488]/5"><div className="overline text-[9px] text-[#0D9488]" data-testid="pay-total-received-label">{VERB_PAST}</div><div className="font-display text-xl font-bold tracking-tight text-[#0D9488]">₹{Number(summary.paid).toLocaleString("en-IN")}</div></div>
            <div className="border border-[#D90429]/30 p-3 bg-[#D90429]/5"><div className="overline text-[9px] text-[#D90429]">Outstanding</div><div className="font-display text-xl font-bold tracking-tight text-[#D90429]" data-testid="outstanding-amount">₹{Number(summary.outstanding).toLocaleString("en-IN")}</div></div>
          </div>
        )}

        <div className="border border-black/10 bg-[#FAFBFC] p-4 mt-3">
          <div className="overline mb-3" data-testid="pay-form-title">{VERB_ADD_TITLE}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Amount (₹) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="rounded-none" data-testid="pay-amount-input"/></div>
            <div><Label className="text-xs">{HDR_DATE} *</Label><Input type="date" value={form.paid_on} onChange={(e) => setForm((f) => ({ ...f, paid_on: e.target.value }))} className="rounded-none" data-testid="pay-date-input"/></div>
            <div><Label className="text-xs" data-testid="pay-by-label">{VERB_BY} *</Label><Input value={form.paid_by} onChange={(e) => setForm((f) => ({ ...f, paid_by: e.target.value }))} className="rounded-none" data-testid="pay-by-input"/></div>
            <div><Label className="text-xs flex items-center gap-1"><Bank size={12}/> Source *</Label>
              <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                <SelectTrigger className="rounded-none" data-testid="pay-source-select"><SelectValue/></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Reference (UTR/Cheque #)</Label><Input value={form.reference_no} onChange={(e) => setForm((f) => ({ ...f, reference_no: e.target.value }))} className="rounded-none" data-testid="pay-ref-input"/></div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-none"/></div>
            {isReceipt && (
              <div className="sm:col-span-2">
                <Label className="text-xs flex items-center gap-1"><Paperclip size={12}/> Payment advice (optional URL / drive link)</Label>
                <Input value={form.advice_url} onChange={(e) => setForm((f) => ({ ...f, advice_url: e.target.value }))} placeholder="Paste link to bank advice / UTR screenshot / drive" className="rounded-none" data-testid="pay-advice-input"/>
              </div>
            )}
          </div>
          <button onClick={save} disabled={saving} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#D90429] text-white text-sm font-bold hover:bg-[#EF233C] disabled:opacity-50" data-testid="pay-save">
            <FloppyDisk size={14} weight="bold"/> {saving ? "Saving…" : VERB_ACTION}
          </button>
        </div>

        {summary?.payments?.length > 0 && (
          <div className="mt-4">
            <div className="overline mb-2" data-testid="pay-history-title">{HISTORY_LABEL}</div>
            <div className="border border-black/10 bg-white max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F5F6F8] text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv">
                  <tr>{["Date","Amount", HDR_BY, "Source","Ref","Advice","Notes",""].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {summary.payments.map((p) => (
                    <tr key={p.id} className="border-t border-black/5" data-testid={`pay-row-${p.id}`}>
                      <td className="px-2 py-1.5 font-mono-shelv">{p.paid_on}</td>
                      <td className="px-2 py-1.5 font-mono-shelv font-bold">₹{Number(p.amount).toLocaleString("en-IN")}</td>
                      <td className="px-2 py-1.5">{p.paid_by}</td>
                      <td className="px-2 py-1.5"><span className="text-[9px] font-mono-shelv uppercase tracking-widest bg-black/5 px-1.5 py-0.5">{p.source}</span></td>
                      <td className="px-2 py-1.5 font-mono-shelv text-[#717680]">{p.reference_no || "—"}</td>
                      <td className="px-2 py-1.5">{p.advice_url ? <a href={p.advice_url} target="_blank" rel="noreferrer" className="text-[#D90429] hover:underline text-[10px] font-mono-shelv uppercase" data-testid={`pay-advice-link-${p.id}`}>View</a> : <span className="text-[#B8BCC7]">—</span>}</td>
                      <td className="px-2 py-1.5 max-w-[140px] truncate text-[#717680]">{p.notes || ""}</td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => remove(p.id)} className="p-1 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`pay-del-${p.id}`}><Trash size={12}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 border border-black/15 text-sm" data-testid="pay-close"><X size={14} weight="bold" className="inline mr-1"/>Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
